/**
 * Provider Agreement Slice
 *
 * Manages publisher agreement acceptance state for organizations.
 * Tracks agreement content from static files, org acceptance status, and
 * provides actions for checking status and recording acceptance.
 *
 * @see GW-339 Provider Agreement acceptance modal
 * @see GW-341 Backend API endpoints (when ready)
 */

import {
  publisherAgreementApi,
  type PublisherAgreementStatus,
  type AcceptAgreementOrganizationDetails,
} from '@/api/publisherAgreement';
import {
  PUBLISHER_AGREEMENT_VERSION,
  PUBLISHER_AGREEMENT_EFFECTIVE_DATE,
  PUBLISHER_AGREEMENT_REQUIRES_REACCEPTANCE,
  PUBLISHER_AGREEMENT_CONTENT,
} from '@/content/legal';

import type { StateCreator } from 'zustand';

export interface AgreementStaticContent {
  version: string;
  effectiveDate: string;
  content: string;
  requiresReacceptance: boolean;
}

export interface PublisherAgreementSlice {
  // State
  publisherAgreementContent: AgreementStaticContent | null;
  publisherAgreementStatus: PublisherAgreementStatus | null;
  publisherAgreementLoading: boolean;
  publisherAgreementError: string | null;
  hasCheckedStatus: Record<string, boolean>; // GW-529: Track per-org check status
  /** GW-2349: True when org has previously accepted but needs to re-accept a newer version */
  publisherAgreementNeedsReacceptance: boolean;

  // Actions
  fetchPublisherAgreement: () => void;
  checkPublisherAgreementStatus: (orgId: string) => Promise<void>;
  /** @see GW-525 Add dynamic form fields for Provider Agreement acceptance */
  acceptPublisherAgreement: (
    orgId: string,
    version: string,
    organizationDetails?: AcceptAgreementOrganizationDetails
  ) => Promise<boolean>;
  clearPublisherAgreementState: () => void;
  clearPublisherAgreementError: () => void;
}

const initialState = {
  publisherAgreementContent: null,
  publisherAgreementStatus: null,
  publisherAgreementLoading: false,
  publisherAgreementError: null,
  hasCheckedStatus: {} as Record<string, boolean>, // GW-529: Track per-org check status
  publisherAgreementNeedsReacceptance: false,
};

/**
 * GW-2349: Compute whether the publisher agreement needs re-acceptance.
 * Mirrors the platformTOSSlice pattern: only flag when the org HAS previously
 * accepted AND the static content has requiresReacceptance=true AND the
 * accepted version differs from the current static content version.
 */
function computeNeedsReacceptance(
  content: AgreementStaticContent | null,
  status: PublisherAgreementStatus | null
): boolean {
  if (!content || !status) return false;
  return (
    content.requiresReacceptance &&
    !!status.acceptedVersion &&
    status.acceptedVersion !== content.version
  );
}

export const createPublisherAgreementSlice: StateCreator<
  PublisherAgreementSlice,
  [],
  [],
  PublisherAgreementSlice
> = (set, get) => ({
  ...initialState,

  /**
   * Fetch publisher agreement content from static files
   * GW-2413: Also update publisherAgreementStatus.currentVersion when content loads
   */
  fetchPublisherAgreement: () => {
    set({ publisherAgreementLoading: true, publisherAgreementError: null });

    const content: AgreementStaticContent = {
      version: PUBLISHER_AGREEMENT_VERSION,
      effectiveDate: PUBLISHER_AGREEMENT_EFFECTIVE_DATE,
      content: PUBLISHER_AGREEMENT_CONTENT,
      requiresReacceptance: PUBLISHER_AGREEMENT_REQUIRES_REACCEPTANCE,
    };

    const { publisherAgreementStatus } = get();
    set({
      publisherAgreementContent: content,
      // GW-2413: Sync currentVersion from static content when publisherAgreementStatus exists
      publisherAgreementStatus: publisherAgreementStatus
        ? { ...publisherAgreementStatus, currentVersion: content.version }
        : publisherAgreementStatus,
      publisherAgreementLoading: false,
      publisherAgreementNeedsReacceptance: computeNeedsReacceptance(
        content,
        publisherAgreementStatus
      ),
    });
  },

  /**
   * Check publisher agreement acceptance status for an organization
   * GW-529: Added guards to prevent infinite re-fetching loops
   * GW-2413: Fixed version mismatch re-fetch logic to prevent localStorage short-circuiting
   * @param orgId - Organization ID to check
   */
  checkPublisherAgreementStatus: async (orgId: string) => {
    const state = get();

    // Guard: Don't fetch if already loading
    if (state.publisherAgreementLoading) {
      return;
    }

    // Guard: Don't fetch if already checked for this org, unless a newer agreement version
    // requires re-acceptance (the cached status may be stale relative to the new version).
    let versionMismatch = false;
    if (state.hasCheckedStatus[orgId]) {
      const content = state.publisherAgreementContent;
      const cachedStatus = state.publisherAgreementStatus;
      versionMismatch = Boolean(
        content?.requiresReacceptance &&
        cachedStatus?.acceptedVersion != null &&
        cachedStatus.acceptedVersion !== content.version
      );
      if (!versionMismatch) {
        return;
      }
    }

    // Guard: Check localStorage directly in case Zustand hasn't hydrated yet
    // This handles E2E tests where mock data is injected before hydration completes.
    // Instead of just returning and waiting for hydration, we immediately populate
    // the in-memory state from localStorage to avoid race conditions (e.g., OrgAppsPage
    // checking publisherAgreementStatus.hasAcceptedCurrent immediately after mount).
    // GW-2413: Skip localStorage guard when versionMismatch is detected to ensure API re-fetch
    if (!versionMismatch) {
      try {
        const storage = localStorage.getItem('gw-storage');
        if (storage) {
          const parsed = JSON.parse(storage);
          if (parsed?.state?.hasCheckedStatus?.[orgId] && parsed?.state?.publisherAgreementStatus) {
            // Immediately populate in-memory state from localStorage
            set((s) => ({
              publisherAgreementStatus: parsed.state.publisherAgreementStatus,
              hasCheckedStatus: { ...s.hasCheckedStatus, [orgId]: true },
            }));
            return;
          }
        }
      } catch {
        // Invalid JSON or localStorage not available, proceed with fetch
      }
    }

    set({ publisherAgreementLoading: true, publisherAgreementError: null });

    try {
      const status = await publisherAgreementApi.getPublisherAgreementStatus(orgId);
      const { publisherAgreementContent } = get();
      set((s) => ({
        publisherAgreementStatus: {
          ...status,
          // currentVersion is not returned by the backend; source it from static content.
          currentVersion: s.publisherAgreementContent?.version ?? status.currentVersion,
        },
        publisherAgreementLoading: false,
        hasCheckedStatus: { ...s.hasCheckedStatus, [orgId]: true }, // GW-529: Mark as checked for this org
        publisherAgreementNeedsReacceptance: computeNeedsReacceptance(
          publisherAgreementContent,
          status
        ),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to check agreement status';
      set((s) => ({
        publisherAgreementLoading: false,
        publisherAgreementError: message,
        hasCheckedStatus: { ...s.hasCheckedStatus, [orgId]: true }, // GW-529: Mark as checked even on error
      }));
    }
  },

  /**
   * Accept publisher agreement for an organization
   * @param orgId - Organization ID accepting the agreement
   * @param version - Version being accepted
   * @param organizationDetails - Organization details for the agreement (GW-525)
   * @returns true if successful, false on error
   * @see GW-525 Add dynamic form fields for Provider Agreement acceptance
   */
  acceptPublisherAgreement: async (
    orgId: string,
    version: string,
    organizationDetails?: AcceptAgreementOrganizationDetails
  ) => {
    set({ publisherAgreementLoading: true, publisherAgreementError: null });

    try {
      const result = await publisherAgreementApi.acceptPublisherAgreement(
        orgId,
        version,
        organizationDetails
      );

      if (result.success) {
        // Update local status to reflect acceptance
        const currentStatus = get().publisherAgreementStatus;
        set({
          publisherAgreementStatus: currentStatus
            ? {
                ...currentStatus,
                hasAcceptedCurrent: true,
                acceptedVersion: version,
                acceptedAt: result.acceptedAt,
                // agreements is preserved via spread of currentStatus
              }
            : {
                hasAcceptedCurrent: true,
                currentVersion: version,
                acceptedVersion: version,
                acceptedAt: result.acceptedAt,
                acceptedBy: null,
                agreements: [],
              },
          publisherAgreementLoading: false,
          publisherAgreementNeedsReacceptance: false,
        });

        // GW-509: Refresh organization data to update currentOrg.stripeConnectedAt
        // This is needed because the publisher onboarding checklist checks currentOrg state
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const refreshCurrentOrganization = (get() as any).refreshCurrentOrganization;
        if (typeof refreshCurrentOrganization === 'function') {
          await refreshCurrentOrganization();
        }

        return true;
      }

      set({ publisherAgreementLoading: false });
      return false;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to accept agreement';
      set({
        publisherAgreementLoading: false,
        publisherAgreementError: message,
      });
      return false;
    }
  },

  /**
   * Clear all publisher agreement state (e.g., on org switch or logout)
   */
  clearPublisherAgreementState: () => {
    set(initialState);
  },

  /**
   * Clear only the error state
   */
  clearPublisherAgreementError: () => {
    set({ publisherAgreementError: null });
  },
});
